<template>
  <div>
    <ToastificationLoadingContent ref="loadingToast" />
    <ToastificationProgressContent ref="progressToast" />

    <Sidenav
      :is-sidebar-active.sync="isSidebarActive"
      :is-add.sync="isAdd"
      :item-edit.sync="itemEdit"
      :server-id="serverId"
      :server-type="serverType"
      @refetch-data="refetchData"
    />

    <b-row class="pt-2 mx-2">
      <b-col cols="6" class="d-flex align-items-center px-0">
        <div class="d-flex justify-content align-items-center">
          <button
            class="btn btn-sm rounded-circle p-25 cursor-pointer"
            :class="isDark ? 'btn-danger' : 'btn-primary'"
            @click="goBack()"
          >
            <i class="feather icon-arrow-left arrow-icon"></i>
          </button>
          <div
            class="ml-1 text-capitalize text-dark cursor-pointer goback-text"
            @click="goBack()"
          >
            {{ $t("go_back") }}
          </div>
        </div>
      </b-col>
      <b-col cols="6" class="text-right px-0">
        <button-filters
          @show-modal="$root.$emit('bv::show::modal', $event)"
          :filters-count="filtersCount"
          @clear-search-filters="clearSearchFilters"
        />
      </b-col>
    </b-row>

    <!-- Filters -->
    <filter-modal @action="buildFilters()" @clear="clearSearchFilters()">
      <template v-slot:main-filters>
        <FilterDevices
          :cameraFilter.sync="cameraFilter"
          :cameraOptions="cameraOptions"
          :statusActiveFilter.sync="statusActiveFilter"
          :camerasOnlineFilter.sync="camerasOnlineFilter"
          :startDateFilter.sync="startDateFilter"
          :endDateFilter.sync="endDateFilter"
        />
      </template>
    </filter-modal>

    <!-- Table Container Card -->
    <b-card class="m-2" no-body>
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Device count-->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <span class="text-muted">
              {{ $tc("ai_analytics_servers.devices_count") }}:
              <strong>{{ devicesCount }}</strong>
            </span>
          </b-col>

          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="$t('table.search')"
              />
              <b-button
                variant="primary"
                @click="openAdd()"
                v-if="$can('create', 'AiAnalyticsServers')"
              >
                <span class="text-nowrap text-capitalize">{{
                  `${$t("actions.add")} ${$tc("ai_analytics_servers.device", 1)}`
                }}</span>
              </b-button>

              <b-button
                variant="secondary"
                class="ml-1"
                @click="restartServer(serverUuid)"
              >
                <span class="text-nowrap text-capitalize">{{
                  $t("ai_analytics_servers.restart_server")
                }}</span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refListTable"
        class="position-relative"
        :items="fetchList"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        :sort-desc.sync="isSortDirDesc"
        show-empty
        :empty-text="$t('no_record_found')"
      >
        <template #head()="data">
          <span> {{ $tc(data.label, 1) }}</span>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button
            :id="`actions-${data.item.id}`"
            tabindex="0"
            variant="white"
            class="px-1 btn-y"
          >
            <feather-icon
              icon="MoreVerticalIcon"
              size="16"
              class="align-middle text-body"
            />
          </b-button>
          <b-popover
            :target="`actions-${data.item.id}`"
            triggers="focus"
            placement="bottom"
            custom-class="popover-p-0"
            variant="white"
          >
            <b-list-group class="m-0">
              <b-list-group-item
                v-if="$can('update', 'AiAnalyticsServers') && serverType === 'fire_smoke'"
                class="border-0"
                button
                @click="openEdit(data.item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.edit") }}
                </span>
              </b-list-group-item>

              <b-list-group-item
                v-if="$can('delete', 'AiAnalyticsServers')"
                class="border-0"
                button
                @click="confirmDelete(data.item)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50 text-capitalize">
                  {{ $t("actions.delete") }}
                </span>
              </b-list-group-item>
            </b-list-group>
          </b-popover>
        </template>

        <!-- Column: Status -->
        <template #cell(online)="data">
          <b-badge :variant="data.item.online ? 'success' : 'danger'">
            {{ data.item.online ? "Online" : "Offline" }}
          </b-badge>
        </template>

        <template #cell(on_server_status)="data">
          <b-form-checkbox
            :checked="data.item.on_server_status === 1"
            switch
            @change="updateStatus(data.item)"
          />
        </template>

        <template #cell(fire_threshold)="data">
          <span
            v-if="
              data.item.fire_threshold !== null && data.item.fire_threshold !== undefined
            "
          >
            {{ Math.round(data.item.fire_threshold * 100) }}%
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(smoke_threshold)="data">
          <span
            v-if="
              data.item.smoke_threshold !== null &&
              data.item.smoke_threshold !== undefined
            "
          >
            {{ Math.round(data.item.smoke_threshold * 100) }}%
          </span>
          <span v-else class="text-muted">-</span>
        </template>

        <template #cell(use_local_confirmation)="data" class="mx-auto">
          <b-badge :variant="data.item.use_local_confirmation ? 'success' : 'secondary'">
            {{ data.item.use_local_confirmation ? $t('yes') : $t('no') }}
          </b-badge>
        </template>
      </b-table>
    </b-card>
  </div>
</template>

<script>
import { ref, computed } from "@vue/composition-api";
import { BFormCheckbox } from "bootstrap-vue";
import useList from "./useList";
import Sidenav from "./Sidenav.vue";
import FilterDevices from "./Filter.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ToastificationLoadingContent from "@core/components/toastification/ToastificationLoadingContent.vue";
import ToastificationProgressContent from "@core/components/toastification/ToastificationProgressContent.vue";
import store from "@/store";
import router from "@/router";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import BreadcrumbMenu from "@/components/BreadcrumbMenu.vue";
import TableFooter from "@/components/TableFooter.vue";
import PerPageSelector from "@/components/PerPageSelector.vue";
import FilterModal from "@/components/Filters/FilterModal.vue";
import ButtonFilters from "@/components/Filters/ButtonFilters.vue";

export default {
  name: "AiAnalyticsDevices",
  components: {
    Sidenav,
    FilterDevices,
    BreadcrumbMenu,
    TableFooter,
    PerPageSelector,
    ToastificationLoadingContent,
    ToastificationProgressContent,
    BFormCheckbox,
    FilterModal,
    ButtonFilters,
  },
  setup() {
    const loadingToast = ref(null);
    const isSidebarActive = ref(false);
    const isAdd = ref(false);
    const itemEdit = ref({});

    const openEdit = (item) => {
      isSidebarActive.value = true;
      itemEdit.value = JSON.parse(JSON.stringify(item));
      isAdd.value = false;
    };

    const { t } = useI18nUtils();
    const toast = useToast();

    const trans = {
      "messages.delete.title": t("messages.delete.title"),
      "messages.delete.body": t("messages.delete.body"),
      "actions.delete": t("actions.delete"),
      "actions.cancel": t("actions.cancel"),
      "ai_analytics_servers.uuid_not_found": t("ai_analytics_servers.uuid_not_found"),
      "ai_analytics_servers.restarted_successfully": t("ai_analytics_servers.restarted_successfully"),
      "ai_analytics_servers.restart_error": t("ai_analytics_servers.restart_error"),
      "ai_analytics_servers.fetch_error": t("ai_analytics_servers.fetch_error"),
      "ai_analytics_servers.delete_error": t("ai_analytics_servers.delete_error"),
      "ai_analytics_servers.update_status_error": t("ai_analytics_servers.update_status_error"),
    };

    const {
      tableColumns,
      fetchList,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      serverId,
      serverType,
      serverUuid,
      devicesCount,
      tenantFilter,
      cameraFilter,
      statusActiveFilter,
      camerasOnlineFilter,
      startDateFilter,
      endDateFilter,
      tenantOptions,
      cameraOptions,
      filtersCount,
      clearSearchFilters,
      buildFilters,
      restartServer,
    } = useList(trans);

    const confirmDelete = function (item) {
      const self = this;
      const storeInner = store;
      this.$swal({
        title: trans["messages.delete.title"],
        text: trans["messages.delete.body"],
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: trans["actions.delete"],
        cancelButtonText: trans["actions.cancel"],
        customClass: {
          confirmButton: "btn btn-primary text-capitalize",
          cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          storeInner
            .dispatch("ai_analytics_servers/devices/delete", {
              id_camera_proxy: item.id,
              id_server: serverId.value,
            })
            .then((response) => {
              if (response.success) {
                toast({
                  component: ToastificationContent,
                  props: {
                    title: String(response.message),
                    icon: "CheckIcon",
                    variant: "success",
                  },
                });
                self.refetchData();
              } else {
                toast(
                  {
                    component: ToastificationContent,
                    props: {
                      title: String(response.message),
                      icon: "AlertTriangleIcon",
                      variant: "danger",
                    },
                  },
                  {
                    timeout: 5000,
                  }
                );
              }
            })
            .catch((response) => {
              toast(
                {
                  component: ToastificationContent,
                  props: {
                    title: String(
                      response.response?.data?.message || trans["ai_analytics_servers.delete_error"]
                    ),
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                },
                {
                  timeout: 5000,
                }
              );
            });
        }
      });
    };

    const updateStatus = (item) => {
      const newStatus = item.on_server_status === 1 ? 0 : 1;
      store
        .dispatch("ai_analytics_servers/devices/updateStatus", {
          id_server: serverId.value,
          id_camera_proxy: item.id,
          status: newStatus,
        })
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: String(response.message),
                icon: "CheckIcon",
                variant: "success",
              },
            });
            refetchData();
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: String(response.message),
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((error) => {
          toast({
            component: ToastificationContent,
            props: {
              title: String(error.response?.data?.message || trans["ai_analytics_servers.update_status_error"]),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const openAdd = () => {
      isAdd.value = true;
      isSidebarActive.value = true;
    };

    const isDark = computed(() => store.state.appConfig.layout.skin === "dark");

    const goBack = () => {
      router.go(-1);
    };

    return {
      isSidebarActive,
      isAdd,
      itemEdit,
      openEdit,
      openAdd,
      confirmDelete,
      restartServer,
      tableColumns,
      fetchList,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      refetchData,
      serverId,
      serverType,
      serverUuid,
      devicesCount,
      updateStatus,
      tenantFilter,
      cameraFilter,
      statusActiveFilter,
      camerasOnlineFilter,
      startDateFilter,
      endDateFilter,
      tenantOptions,
      cameraOptions,
      filtersCount,
      clearSearchFilters,
      buildFilters,
      isDark,
      goBack,
    };
  },
};
</script>
